import { useEffect, useRef, useState } from "react";
import { CYCLE_ROLE, WEEK_STATUS } from "../domain/insightsPresentation";
import { COMPACT_WEEK_LABELS_AFTER, axisLabelIndexes, maxAxisLabels } from "../domain/chartAxes";

/*
 * Weekly line chart for Insights (Adherence weekly sets, Muscle Volume).
 * Pure presentation: it plots the values it is given, one column per week, and
 * never aggregates. A null value is a gap (no data), never a zero. The chart is
 * decorative for assistive technology; `summary` renders the same figures as a
 * visually hidden table.
 */

const HEIGHT = 156;
// Room for the PREV / CURR CYCLE labels only when the columns carry cycles.
const PAD_TOP = 12;
const PAD_TOP_SEGMENTED = 26;
const PAD_BOTTOM = 22;
const PAD_LEFT = 26;
// Room for a vertical y-axis title ("Sets") left of the ticks, and for an
// x-axis title ("Weeks") under the week labels.
const PAD_LEFT_TITLED = 36;
const X_TITLE_SPACE = 14;
const PAD_RIGHT = 10;
const STEPS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 80, 100];
const DENSE_COLUMNS = 30;
const LABELLED_COLUMNS = 16;
const WIDE_CHART = 560;

function niceStep(raw) {
  const found = STEPS.find((step) => step >= raw);
  if (found) return found;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return Math.ceil(raw / magnitude) * magnitude;
}

function useElementWidth(fallback = 320) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => {
      const next = Math.round(element.getBoundingClientRect().width);
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

function Marker({ shape, x, y, className }) {
  if (shape === "square") {
    return <rect className={className} x={x - 3.5} y={y - 3.5} width={7} height={7} />;
  }
  if (shape === "diamond") {
    return <path className={className} d={`M${x} ${y - 4.5}L${x + 4.5} ${y}L${x} ${y + 4.5}L${x - 4.5} ${y}Z`} />;
  }
  return <circle className={className} cx={x} cy={y} r={3.5} />;
}

// Consecutive non-null runs, so a missing week breaks the line.
function runs(points) {
  const result = [];
  let current = [];
  points.forEach((point) => {
    if (point.y === null) {
      if (current.length) result.push(current);
      current = [];
    } else {
      current.push(point);
    }
  });
  if (current.length) result.push(current);
  return result;
}

// Short cycle labels (W1…W12) fit one per column; calendar dates are thinned
// to a handful (4–5 on a phone) so they never overlap.
// Long cycle timelines, or columns narrower than this, show the bare week
// number ("3", not "W3"); the cycle is already named above the plot.
const COMPACT_SLOT = 20;

function axisLabel(column, compact) {
  return compact && column.shortLabel ? column.shortLabel : column.label;
}

function labelIndexes(columns, plotWidth, wide, compact) {
  const longest = Math.max(0, ...columns.map((column) => String(axisLabel(column, compact) || "").length));
  if (longest <= 3 && columns.length <= LABELLED_COLUMNS) return new Set(columns.map((_, index) => index));
  return new Set(axisLabelIndexes(columns.length, maxAxisLabels(plotWidth, longest, wide ? 8 : 5)));
}

function labelHalf(label) {
  return String(label || "").length * 3 + 2;
}

// Keeps an edge label inside the canvas instead of clipping it.
function labelX(x, label, width) {
  const half = labelHalf(label);
  return Math.min(Math.max(x, half), width - half);
}

export default function InsightsLineChart({
  columns,
  series,
  highlightIndex = null,
  title,
  summary,
  formatValue = (value) => String(value),
  xTitle = null,
  yTitle = null,
  testId,
}) {
  const [ref, width] = useElementWidth();
  const padLeft = yTitle ? PAD_LEFT_TITLED : PAD_LEFT;
  const height = HEIGHT + (xTitle ? X_TITLE_SPACE : 0);
  const labelY = height - 6 - (xTitle ? X_TITLE_SPACE : 0);
  const plotWidth = Math.max(1, width - padLeft - PAD_RIGHT);
  const padTop = columns.some((column) => column.segment) ? PAD_TOP_SEGMENTED : PAD_TOP;
  const plotHeight = height - padTop - PAD_BOTTOM - (xTitle ? X_TITLE_SPACE : 0);
  const count = columns.length;
  const slot = count > 0 ? plotWidth / count : plotWidth;
  const xOf = (index) => padLeft + slot * index + slot / 2;

  const allValues = series.flatMap((entry) => entry.values.filter((value) => value !== null));
  const maxValue = Math.max(0, ...allValues);
  const step = niceStep(Math.max(maxValue, 3) / 3);
  const yMax = step * 3;
  const yOf = (value) => padTop + plotHeight - (value / yMax) * plotHeight;
  const ticks = [0, step, step * 2, step * 3];
  const dense = count > DENSE_COLUMNS;
  const compact = count > COMPACT_WEEK_LABELS_AFTER || slot < COMPACT_SLOT;
  const labelled = labelIndexes(columns, plotWidth, width >= WIDE_CHART, compact);

  const boundary = columns.findIndex((column, index) => index > 0
    && column.segment === CYCLE_ROLE.ACTIVE
    && columns[index - 1].segment === CYCLE_ROLE.PREVIOUS);
  const previousEnd = boundary > 0 ? boundary : -1;
  const hasPrevious = columns.some((column) => column.segment === CYCLE_ROLE.PREVIOUS);
  const activeStart = columns.findIndex((column) => column.segment === CYCLE_ROLE.ACTIVE);
  // A cycle's name starts where its first week label starts.
  const segmentX = (index) => {
    const label = axisLabel(columns[index], compact);
    return labelX(xOf(index), label, width) - labelHalf(label) + 2;
  };

  return (
    <figure className="lz-insights-chart" data-testid={testId}>
      {title ? <figcaption className="lz-insights-chart__title">{title}</figcaption> : null}
      <div className="lz-insights-chart__canvas" ref={ref}>
        <svg
          className="lz-insights-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          aria-hidden="true"
          focusable="false"
        >
          {highlightIndex !== null && highlightIndex >= 0 && highlightIndex < count ? (
            <rect
              className="lz-insights-chart__highlight"
              x={padLeft + slot * highlightIndex + slot * 0.18}
              y={padTop - 6}
              width={Math.max(2, slot * 0.64)}
              height={plotHeight + 6}
              rx={4}
            />
          ) : null}

          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className="lz-insights-chart__grid"
                x1={padLeft}
                x2={width - PAD_RIGHT}
                y1={yOf(tick)}
                y2={yOf(tick)}
              />
              <text className="lz-insights-chart__tick" x={padLeft - 6} y={yOf(tick) + 3} textAnchor="end">
                {tick}
              </text>
            </g>
          ))}

          {previousEnd > 0 ? (
            <line
              className="lz-insights-chart__divider"
              x1={padLeft + slot * previousEnd}
              x2={padLeft + slot * previousEnd}
              y1={padTop - 14}
              y2={padTop + plotHeight}
            />
          ) : null}
          {hasPrevious ? (
            <text
              className="lz-insights-chart__segment"
              x={segmentX(0)}
              y={12}
              textAnchor="start"
            >
              PREV CYCLE
            </text>
          ) : null}
          {activeStart >= 0 ? (
            <text
              className="lz-insights-chart__segment lz-insights-chart__segment--active"
              x={segmentX(activeStart)}
              y={12}
              textAnchor="start"
            >
              CURR CYCLE
            </text>
          ) : null}

          {series.map((entry) => {
            const points = entry.values.map((value, index) => ({
              x: xOf(index),
              y: value === null || value === undefined ? null : yOf(value),
            }));
            const segments = runs(points);
            return (
              <g key={entry.key} className="lz-insights-chart__series" data-tone={entry.tone}>
                {entry.area ? segments.filter((run) => run.length > 1).map((run, index) => (
                  <path
                    key={`area-${index}`}
                    className="lz-insights-chart__area"
                    d={`M${run[0].x} ${yOf(0)} ${run.map((point) => `L${point.x} ${point.y}`).join(" ")} L${run[run.length - 1].x} ${yOf(0)}Z`}
                  />
                )) : null}
                {segments.map((run, index) => (
                  <path
                    key={`line-${index}`}
                    className={`lz-insights-chart__line${entry.secondary ? " lz-insights-chart__line--secondary" : ""}`}
                    d={run.map((point, pointIndex) => `${pointIndex === 0 ? "M" : "L"}${point.x} ${point.y}`).join(" ")}
                  />
                ))}
                {!dense && entry.marker
                  ? points.map((point, index) => (point.y === null ? null : (
                    <Marker
                      key={`marker-${index}`}
                      shape={entry.marker}
                      x={point.x}
                      y={point.y}
                      className={`lz-insights-chart__marker${index === highlightIndex ? " lz-insights-chart__marker--current" : ""}`}
                    />
                  )))
                  : null}
              </g>
            );
          })}

          {columns.map((column, index) => (labelled.has(index) ? (
            <text
              key={column.key}
              className={`lz-insights-chart__label${column.status === WEEK_STATUS.UPCOMING ? " lz-insights-chart__label--upcoming" : ""}${index === highlightIndex ? " lz-insights-chart__label--current" : ""}`}
              x={labelX(xOf(index), axisLabel(column, compact), width)}
              y={labelY}
              textAnchor="middle"
            >
              {axisLabel(column, compact)}
            </text>
          ) : null))}

          {xTitle ? (
            <text className="lz-insights-chart__axis-title" x={padLeft + plotWidth / 2} y={height - 3} textAnchor="middle">
              {xTitle}
            </text>
          ) : null}
          {yTitle ? (
            <text
              className="lz-insights-chart__axis-title"
              transform={`translate(10 ${padTop + plotHeight / 2}) rotate(-90)`}
              textAnchor="middle"
            >
              {yTitle}
            </text>
          ) : null}
        </svg>
      </div>
      {summary ? (
        <div className="lz-v2-visually-hidden">
          <table>
          <caption>{summary}</caption>
          <thead>
            <tr>
              <th scope="col">Week</th>
              {series.map((entry) => <th scope="col" key={entry.key}>{entry.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {columns.map((column, index) => (
              <tr key={column.key}>
                <th scope="row">{column.longLabel}</th>
                {series.map((entry) => (
                  <td key={entry.key}>
                    {entry.values[index] === null || entry.values[index] === undefined
                      ? "No data"
                      : formatValue(entry.values[index])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      ) : null}
    </figure>
  );
}
